import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Send, PartyPopper, Flag, ExternalLink, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reply = ({ r }) => (
  <div className={`rounded-lg px-3 py-2 text-sm ${r.from_player ? 'bg-[#8B5A3C]/10 ml-6' : 'bg-[var(--hover-bg-subtle)] mr-6'}`} data-testid="help-reply">
    <p className="text-[11px] font-semibold text-[#8B5A3C] mb-0.5">{r.from_player ? 'You' : r.author}</p>
    <p className="text-[var(--text-primary)] whitespace-pre-wrap break-words leading-relaxed">{r.content}</p>
  </div>
);

const ThreadActions = ({ req, onChange }) => {
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    setBusy(kind);
    try {
      await axios.post(`${API_URL}/api/help-requests/${req.id}/${kind}`, {}, { withCredentials: true });
      toast.success(kind === 'resolve' ? 'Brilliant! We let the community know you cracked it.' : 'Reported. Our team will review this puzzle with all its details.');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" onClick={() => act('resolve')} disabled={!!busy} className="rounded-full bg-[#34C759]/90 hover:bg-[#34C759] text-white h-8" data-testid="help-resolve-btn">
        <PartyPopper className="w-3.5 h-3.5 mr-1" /> I solved it
      </Button>
      {req.community_reply_count > 0 && (
        <Button size="sm" variant="outline" onClick={() => act('escalate')} disabled={!!busy} className="rounded-full h-8 border-[#FF453A]/40 text-[#FF453A]" data-testid="help-escalate-btn">
          <Flag className="w-3.5 h-3.5 mr-1" /> Still stuck: report puzzle
        </Button>
      )}
    </div>
  );
};

export const HelpThreadPanel = ({ req, onClose, onChange, className = '' }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [req?.replies?.length]);
  if (!req) return null;

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/help-requests/${req.id}/reply`, { content: text.trim() }, { withCredentials: true });
      setText('');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your reply.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`help-thread-panel flex flex-col rounded-2xl border border-[var(--border-subtle)] shadow-xl backdrop-blur-md bg-[var(--background-main)]/95 ${className}`} data-testid="help-thread-panel">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-[var(--border-subtle)]">
        <p className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-1.5"><Users className="w-4 h-4 text-[#8B5A3C]" /> Community help</p>
        <div className="flex items-center gap-1">
          {req.thread_url && (
            <a href={req.thread_url} target="_blank" rel="noreferrer noopener" className="p-1.5 rounded-full text-[var(--text-muted)] hover:text-[#8B5A3C]" title="Open on Discord" data-testid="help-thread-discord-link">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <button onClick={onClose} className="p-1.5 rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)]" aria-label="Close help panel" data-testid="help-thread-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[80px]" data-testid="help-thread-replies">
        {req.replies.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] leading-relaxed" data-testid="help-thread-waiting">
            {req.posted ? 'Your grid is with the community. Replies appear here as soon as someone answers. Keep playing in the meantime.' : 'Sending your request to the community…'}
          </p>
        ) : req.replies.map((r) => <Reply key={r.id} r={r} />)}
        <div ref={endRef} />
      </div>
      {req.status === 'open' ? (
        <div className="px-4 pb-3 pt-2 border-t border-[var(--border-subtle)] space-y-2">
          {req.posted && (
            <div className="flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 1500))}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="Reply to the thread…"
                className="flex-1 min-w-0 rounded-full px-3 py-1.5 text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] text-[var(--text-primary)] outline-none focus:border-[#8B5A3C]"
                data-testid="help-reply-input"
              />
              <button onClick={send} disabled={sending || !text.trim()} className="p-2 rounded-full bg-[#8B5A3C] text-[#FFF3E4] disabled:opacity-40" aria-label="Send reply" data-testid="help-reply-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}
          <ThreadActions req={req} onChange={onChange} />
        </div>
      ) : (
        <p className="px-4 pb-3 text-xs text-[var(--text-muted)]" data-testid="help-thread-status">
          {req.status === 'escalated' ? 'Reported to our team. Thank you for sticking with it.' : 'Solved. Nice work!'}
        </p>
      )}
    </div>
  );
};
